import { LoadingButton } from "@mui/lab";
import { Box, Collapse, Typography } from "@mui/material";
import AccordionCard from "components/AccordionCard";
import DatePickerControl from "components/form/DatePickerControl";
import SelectControl from "components/form/SelectControl";
import TextFieldControl from "components/form/TextFieldControl";
import { ORGANIZATION_STATES, ORGANIZATION_TYPES } from "constants/company";
import VALIDATION from "helpers/validation";
import {
  FIELD_LABEL_PROPS,
  KPP_RULES,
  OGRN_RULES,
  OKATO_RULES,
  OKPO_RULES,
  OKTMO_RULES,
  SELECT_FIELD_SX,
} from "../constants";
import { InnLookupState } from "../types";
import StepSection from "./StepSection";

type CompanyDetailsStepProps = {
  innLookupState: InnLookupState;
  isInnLookupLoading: boolean;
  onLookupByInn: () => void;
  onNextStep: () => void;
};

const CompanyDetailsStep = ({
  innLookupState,
  isInnLookupLoading,
  onLookupByInn,
  onNextStep,
}: CompanyDetailsStepProps) => (
  <StepSection title='Реквизиты компании'>
    <TextFieldControl
      name='inn'
      label='ИНН'
      variant='outlined'
      labelTypographyProps={FIELD_LABEL_PROPS}
      placeholder='10 или 12 цифр'
      rules={{
        required: true,
        ...VALIDATION.inn,
        validate: (value) => {
          if (typeof value !== "string") return false;

          const validationResult = VALIDATION.inn.validate(value);
          if (validationResult !== true) return validationResult;

          const innLength = value.trim().length;
          return innLength === 10 || innLength === 12 || "ИНН должен содержать 10 или 12 цифр";
        },
      }}
      endAction={
        <LoadingButton
          loading={isInnLookupLoading}
          variant='outlined'
          size='small'
          sx={{ minWidth: 104, height: 40, borderRadius: 1.5 }}
          onClick={onLookupByInn}>
          Найти
        </LoadingButton>
      }
    />

    <Collapse in={innLookupState !== "idle"}>
      <Box
        sx={{
          borderRadius: 2.5,
          border: "1px solid",
          borderColor: innLookupState === "success" ? "#B9C8FF" : "#FFD7A0",
          backgroundColor: innLookupState === "success" ? "#EEF3FF" : "#FFF8EA",
          px: 2.5,
          py: 2.25,
        }}>
        <Typography sx={{ color: innLookupState === "success" ? "#2147AF" : "#D97706", fontWeight: 700, fontSize: 18 }}>
          {innLookupState === "success" ? "Данные найдены по ИНН" : "Данные по ИНН не найдены"}
        </Typography>
        <Typography
          sx={{
            mt: 0.75,
            color: innLookupState === "success" ? "#2147AF" : "#B45309",
            fontSize: 17,
            lineHeight: 1.45,
          }}>
          {innLookupState === "success"
            ? "Проверьте и при необходимости отредактируйте."
            : "Заполните реквизиты вручную."}
        </Typography>
      </Box>
    </Collapse>

    <TextFieldControl
      name='fullName'
      label='Полное наименование'
      variant='outlined'
      labelTypographyProps={FIELD_LABEL_PROPS}
      multiline
      placeholder='Общество с ограниченной ответственностью'
      rules={{ required: true, ...VALIDATION.companyName }}
    />

    <TextFieldControl
      name='shortName'
      label='Краткое наименование'
      variant='outlined'
      labelTypographyProps={FIELD_LABEL_PROPS}
      placeholder='ООО «Компания»'
      rules={{ required: true, ...VALIDATION.companyName }}
    />

    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
        gap: 2,
      }}>
      <TextFieldControl
        name='kpp'
        label='КПП'
        variant='outlined'
        labelTypographyProps={FIELD_LABEL_PROPS}
        placeholder='9 цифр'
        rules={{ required: true, ...KPP_RULES }}
      />

      <TextFieldControl
        name='ogrn'
        label='ОГРН'
        variant='outlined'
        labelTypographyProps={FIELD_LABEL_PROPS}
        placeholder='13 цифр'
        rules={{ required: true, ...OGRN_RULES }}
      />
    </Box>

    <TextFieldControl
      name='okpo'
      label='ОКПО'
      variant='outlined'
      labelTypographyProps={FIELD_LABEL_PROPS}
      placeholder='8, 10 или 14 цифр'
      rules={{ required: true, ...OKPO_RULES }}
    />

    <AccordionCard
      title='Дополнительные реквизиты'
      accordionProps={{
        sx: {
          border: "1px solid #E2E8F0",
          boxShadow: "none",
          maxHeight: "none",
          "& .MuiAccordionSummary-root": {
            minHeight: "56px !important",
            borderBottom: "1px solid #E2E8F0",
            backgroundColor: "#F8FAFF",
            px: 2.5,

            "& .MuiTypography-root": {
              color: "base.500",
              fontWeight: 600,
            },
          },
          "& .MuiAccordionDetails-root": {
            px: 2.5,
            pt: 0,
            pb: 2.5,
          },
        },
      }}>
      <Box sx={{ display: "grid", gap: 2.5, pt: 2.5 }}>
        <Typography
          sx={{
            fontSize: 14,
            lineHeight: 1.5,
            p: 2,
            backgroundColor: "#FFF9EF",
            color: "#D97706",
            border: "1px solid #FFD28A",
            borderRadius: 2,
          }}>
          Можно не заполнять сейчас — эти данные пригодятся при коммерческом использовании продукта (договор, счета,
          акты).
        </Typography>

        <TextFieldControl
          name='registrationAddress'
          label='Адрес организации'
          variant='outlined'
          labelTypographyProps={FIELD_LABEL_PROPS}
          multiline
          placeholder='Юридический адрес'
          rules={{ ...VALIDATION.address }}
        />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
          }}>
          <TextFieldControl
            name='okato'
            label='ОКАТО'
            variant='outlined'
            labelTypographyProps={FIELD_LABEL_PROPS}
            placeholder='ОКАТО'
            rules={{ ...OKATO_RULES }}
          />

          <TextFieldControl
            name='oktmo'
            label='ОКТМО'
            variant='outlined'
            labelTypographyProps={FIELD_LABEL_PROPS}
            placeholder='ОКТМО'
            rules={{ ...OKTMO_RULES }}
          />
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
          }}>
          <DatePickerControl
            name='registrationDate'
            label='Дата регистрации'
            variant='outlined'
            maxDate={new Date()}
            labelProps={FIELD_LABEL_PROPS}
          />

          <DatePickerControl
            name='ogrnDate'
            label='Дата выдачи ОГРН'
            variant='outlined'
            maxDate={new Date()}
            labelProps={FIELD_LABEL_PROPS}
          />
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
          }}>
          <SelectControl
            name='state'
            label='Статус организации'
            placeholder='Выберите статус'
            items={ORGANIZATION_STATES}
            valueName='id'
            labelName='displayName'
            variant='outlined'
            sx={SELECT_FIELD_SX}
            labelProps={FIELD_LABEL_PROPS}
          />

          <SelectControl
            name='type'
            label='Тип организации'
            placeholder='Выберите тип'
            items={ORGANIZATION_TYPES}
            valueName='id'
            labelName='displayName'
            variant='outlined'
            sx={SELECT_FIELD_SX}
            labelProps={FIELD_LABEL_PROPS}
          />
        </Box>

        <TextFieldControl
          name='contractNumber'
          label='Номер договора'
          variant='outlined'
          labelTypographyProps={FIELD_LABEL_PROPS}
          placeholder='№ договора'
        />
      </Box>
    </AccordionCard>

    <LoadingButton
      loading={isInnLookupLoading}
      variant='contained'
      size='large'
      fullWidth
      onClick={onNextStep}>
      Далее
    </LoadingButton>
  </StepSection>
);

export default CompanyDetailsStep;
